import { ColumnDef, Row } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import { Keyboard, Video, Volume2 } from 'lucide-react';
import UserFilledIcon from '@/components/icons/UserFilledIcon';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import useTranslation from '@/hooks/useTranslation';
import { stripMarkdown } from '@/utils/stringHelpers';
import { AllTranscriptsProps } from '@/types';
import { TranscriptsView } from '@/enums';
import TranscriptsColumnsHeaderButton from './TranscriptsColumnsHeaderButton';
import TranscriptsColumnsCell from './TranscriptsColumnsCell';
import QualityBadge from '@/components/ui/QualityBadge';
import { formatMetadataCellValue } from '@/utils/metadataDisplay';

const formatColumnName = (key: string) =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const TranscriptsColumns = (
  metadataKeysArray: string[],
  activeView: TranscriptsView = TranscriptsView.COMPLETED,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useTranslation();

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const getColor = (row: Row<AllTranscriptsProps>) => {
    if (row?.original?.inRun === true && !row?.original?.language) return '#059669';
    if (row?.original?.language) return '#737373';
    return '#f97316';
  };

  const isScreenedOut = activeView === TranscriptsView.SCREENED_OUT;

  const columns: ColumnDef<AllTranscriptsProps>[] = [
    {
      accessorKey: 'participants',
      // Screened-out rows have no respondent_index (they never entered the
      // interview), so any number shown here would be a fabricated page
      // ordinal. The Conversation ID column is the real row identifier for
      // that view, so the label drops the number and sorting is disabled —
      // sorting by a fake ordinal would be actively misleading, and the
      // backend has nothing meaningful to sort by here either.
      enableSorting: !isScreenedOut,
      header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.participant')} />,
      cell: ({ row }) => {
        if (isScreenedOut) {
          return (
            <div className="flex items-center h-full gap-3 whitespace-nowrap">
              <div className="flex items-center gap-3 font-semibold">
                <div style={{ color: getColor(row) }}>
                  <UserFilledIcon className="size-7" />
                </div>
                <p>{lang.get('msg.participant')}</p>
              </div>
            </div>
          );
        }

        const participantLabel =
          row?.original?.respondent_index != null
            ? `${lang.get('msg.participant')} ${row.original.respondent_index}`
            : `${lang.get('msg.participant')} ${(row?.index ?? 0) + 1}`;

        return (
          <button
            type="button"
            className="flex items-center h-full gap-3 group whitespace-nowrap"
            onClick={() =>
              addQueryParams({
                selected_transcript: row?.original?.conversation_id ?? row?.original?._id,
              })
            }
          >
            <div className="flex items-center gap-3 font-semibold transition-all ease-in-out group-hover:underline">
              <div style={{ color: getColor(row) }}>
                <UserFilledIcon className="size-7" />
              </div>
              <p>{participantLabel}</p>
              <div className="flex items-center ml-2 gap-1.5 text-black/80 whitespace-nowrap">
                {row?.original?.messages?.some(
                  (item) => item?.text !== '' && item?.role === 'user',
                ) && (
                  <TooltipWrapper text="Text response">
                    <Keyboard className="h-5 w-5" />
                  </TooltipWrapper>
                )}
                {row?.original?.messages?.some(
                  (message) =>
                    message?.data?.some((dataItem: File) => dataItem.type === 'audio') &&
                    message.role === 'user',
                ) && (
                  <TooltipWrapper text="Audio response">
                    <Volume2 className="h-5 w-5" />
                  </TooltipWrapper>
                )}
                {row?.original?.messages?.some(
                  (message) =>
                    message?.data?.some((dataItem: File) => dataItem.type === 'video') &&
                    message.role === 'user',
                ) && (
                  <TooltipWrapper text="Video response">
                    <Video className="h-5 w-5" />
                  </TooltipWrapper>
                )}
              </div>
            </div>
          </button>
        );
      },
    },
    {
      accessorFn: (row) => row.conversation_id || row._id,
      id: 'conversation_id',
      enableSorting: false,
      header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.conversationID')} />,
      cell: ({ row }) => (
        <TranscriptsColumnsCell
          text={row?.original?.conversation_id ?? row?.original?._id}
          onClick={
            isScreenedOut
              ? undefined
              : () =>
                  addQueryParams({
                    selected_transcript: row?.original?.conversation_id ?? row?.original?._id,
                  })
          }
        />
      ),
    },
    {
      // NOTE: the table rows are prepared by TranscriptsTable's
      // `conversationsWithDuration`, which overwrites `createdAt` with the
      // formatted `startedAt || createdAt`. So this column displays the
      // interview start time for Completed/Incomplete rows and the creation
      // time only for Screened Out rows (which never started). The column id
      // stays `startedAt` for URL/visibility state; the wire sort key is
      // mapped per view in ConversationsTranscripts (TELV1-560).
      accessorKey: 'createdAt',
      id: 'startedAt',
      header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.startTime')} />,
      cell: ({ row }) => (
        <TranscriptsColumnsCell
          text={row?.getValue('startedAt')}
          onClick={
            isScreenedOut
              ? undefined
              : () => addQueryParams({ selected_transcript: row?.original?._id })
          }
        />
      ),
    },
    ...(activeView !== TranscriptsView.INCOMPLETE && !isScreenedOut
      ? [
          {
            accessorKey: 'duration' as const,
            enableSorting: false,
            header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.duration')} />,
            cell: ({ row }: { row: Row<AllTranscriptsProps> }) => (
              <TranscriptsColumnsCell
                text={row?.getValue('duration') !== 'NaN:NaN:NaN' ? row?.getValue('duration') : '/'}
                onClick={() => addQueryParams({ selected_transcript: row?.original?._id })}
              />
            ),
          },
        ]
      : []),
    // TELV1-433 — LLM-judged quality category with the judge's reason on
    // hover. Only completed conversations are ever scored, so the column
    // exists only on the completed view.
    ...(activeView !== TranscriptsView.INCOMPLETE && !isScreenedOut
      ? [
          {
            accessorFn: (row: AllTranscriptsProps) => row?.quality?.category || '',
            id: 'quality',
            enableSorting: false,
            header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.quality')} />,
            cell: ({ row }: { row: Row<AllTranscriptsProps> }) => (
              <QualityBadge quality={row?.original?.quality} />
            ),
          },
        ]
      : []),
    ...(activeView !== TranscriptsView.INCOMPLETE && !isScreenedOut
      ? [
          {
            accessorFn: (row: AllTranscriptsProps) => row?.digest?.summary || '',
            id: 'digest.summary',
            enableSorting: false,
            header: () => <TranscriptsColumnsHeaderButton text={lang.get('msg.summary')} />,
            cell: ({ row }: { row: Row<AllTranscriptsProps> }) => {
              const rawSummary = row?.original?.digest?.summary || '/';
              const plainTextSummary = rawSummary === '/' ? '/' : stripMarkdown(rawSummary);

              return (
                <TooltipWrapper text={plainTextSummary} className="max-w-[700px] text-xs z-[100]">
                  <p className="min-w-[150px] max-w-[500px] truncate">{plainTextSummary}</p>
                </TooltipWrapper>
              );
            },
          },
        ]
      : []),
  ];

  if (metadataKeysArray.length) {
    metadataKeysArray.forEach((key: string) => {
      columns.push({
        accessorKey: `metadata.${key}`,
        id: `metadata.${key}`,
        header: () => <TranscriptsColumnsHeaderButton text={formatColumnName(key)} />,
        cell: ({ row }) => (
          <TranscriptsColumnsCell
            text={formatMetadataCellValue(row.original.metadata?.[key])}
            onClick={() => addQueryParams({ selected_transcript: row?.original?._id })}
            additionalClasses="flex items-center justify-start w-full h-full"
          />
        ),
      });
    });
  }

  return columns;
};

export default TranscriptsColumns;
